import { useEffect } from 'react'
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { supabase } from './supabase'
import { getCurrentGeoSession } from './geoSession'

const dsn = import.meta.env.VITE_SENTRY_DSN

if (dsn) {
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.elementTimingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })
}

export type SentryRole = 'admin' | 'owner' | 'user' | 'geo' | 'anon'

function applyDeviceContext(): void {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua)
  const connection = (navigator as { connection?: { effectiveType?: string; type?: string } })
    .connection
  Sentry.setTag('device', isMobile ? 'mobile' : 'desktop')
  Sentry.setContext('device', {
    screen: `${window.screen?.width ?? 0}x${window.screen?.height ?? 0}`,
    has_geolocation: typeof navigator !== 'undefined' && typeof navigator.geolocation !== 'undefined',
    connection_type: connection?.effectiveType ?? connection?.type ?? null,
  })
}

/**
 * Syncs the current Sentry user context to the known identity so errors,
 * traces, and replays can be attributed to a person (or an anonymous visitor).
 * Also stamps device context (mobile/desktop, geolocation availability, screen
 * size) that is useful for debugging GPS-based flows.
 *
 * - Logged-in users bind to their account id + email.
 * - Unauthenticated users with an active geo session are bound by their opaque
 *   geo session token, letting operators correlate an anonymous replay back to
 *   the `geo_sessions` row in the DB.
 * - Everyone else is cleared back to anonymous.
 *
 * `role` defaults to `user`/`geo`/`anon` based on the resolved identity; pass an
 * explicit value (from the calling context, e.g. Gallery ownership checks) to
 * override it.
 */
export async function syncSentryUser(role?: SentryRole): Promise<void> {
  if (!dsn) return
  applyDeviceContext()
  const { data: { user } } = await supabase.auth.getUser()
  if (user?.id) {
    Sentry.setUser({ id: user.id, email: user.email ?? undefined, ip_address: '{{auto}}' })
    Sentry.setTag('role', role ?? 'user')
    return
  }
  const geo = getCurrentGeoSession()
  if (geo) {
    Sentry.setUser({ id: geo.token, username: 'geo', ip_address: '{{auto}}' })
    Sentry.setTag('role', role ?? 'geo')
    return
  }
  Sentry.setUser(null)
  Sentry.setTag('role', role ?? 'anon')
}

interface GalleryContextParams {
  galleryId: string | null | undefined
  galleryName: string
  shortCode?: string
  isOwner?: boolean
  isAdmin?: boolean
  role: SentryRole
}

/**
 * Stamps the currently-viewed gallery so errors/replays can be attributed to a
 * specific gallery. Tagging `gallery_id` + `gallery_short_code` makes issues
 * filterable; the structured `gallery` context provides readable detail.
 *
 * Passing a falsy `galleryId` clears the gallery context (e.g. when leaving the
 * gallery route or signing out).
 */
export function syncSentryGalleryContext(params: GalleryContextParams): void {
  if (!dsn) return
  if (!params.galleryId) {
    Sentry.setTag('gallery_id', '')
    Sentry.setTag('gallery_short_code', '')
    Sentry.setContext('gallery', null)
    return
  }
  Sentry.setTag('gallery_id', params.galleryId)
  Sentry.setTag('gallery_short_code', params.shortCode ?? '')
  Sentry.setTag('role', params.role)
  Sentry.setContext('gallery', {
    gallery_id: params.galleryId,
    name: params.galleryName,
    short_code: params.shortCode ?? '',
    is_owner: !!params.isOwner,
    is_admin: !!params.isAdmin,
  })
}

export { Sentry }
